import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Sparkles, MapPin, Loader2 } from 'lucide-react';
import { trackFormSubmit } from '../../services/landingAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadGenForm = () => {
  const [form, setForm] = useState({
    name: '',
    society_name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [geo, setGeo] = useState({ lat: null, lon: null, label: null });
  const [geocoding, setGeocoding] = useState(false);
  const lastQueryRef = useRef('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Debounced geocoding via backend proxy (cached, rate-limited)
  useEffect(() => {
    const q = (form.address || '').trim();
    if (q.length < 4) {
      setGeo({ lat: null, lon: null, label: null });
      return;
    }
    const handle = setTimeout(async () => {
      if (q === lastQueryRef.current) return;
      lastQueryRef.current = q;
      setGeocoding(true);
      try {
        const res = await axios.post(`${API}/geocode`, null, {
          params: {
            q:
              q +
              (q.match(
                /india|bengaluru|bangalore|mumbai|delhi|chennai|hyderabad|pune/i
              )
                ? ''
                : ', India'),
          },
        });
        if (res.data?.ok) {
          setGeo({
            lat: res.data.lat,
            lon: res.data.lon,
            label: res.data.label,
          });
        } else {
          setGeo({ lat: null, lon: null, label: null });
        }
      } catch {
        setGeo({ lat: null, lon: null, label: null });
      } finally {
        setGeocoding(false);
      }
    }, 700);
    return () => clearTimeout(handle);
  }, [form.address]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.society_name) {
      toast.error('Please fill name, society and email');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        latitude: geo.lat,
        longitude: geo.lon,
        geocoded_label: geo.label,
      };
      await axios.post(`${API}/leads/submit`, payload);
      trackFormSubmit('lead-form');
      setSubmitted(true);
      toast.success('Thanks! We\'ll be in touch within a day.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="demo"
      className="py-24 lg:py-32 bg-white"
      data-testid="lead-gen-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left value prop */}
          <div className="lg:col-span-5">
            <p
              className="text-base md:text-lg tracking-wide uppercase text-slate-500 font-semibold mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Book a 20-minute walkthrough
            </p>
            <h3
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold leading-tight text-slate-900"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="lead-gen-headline"
            >
              See it on your{' '}
              <span className="text-[#14D4C6]">society&apos;s data</span>.
            </h3>
            <p
              className="mt-5 text-base text-slate-600 leading-relaxed"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Tell us about your community. We&apos;ll set up a sandbox for you to
              click around with your own villas, residents and gate config. No card,
              no obligation.
            </p>
            <ul
              className="mt-8 space-y-3"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              {[
                'Personalised setup with your villa list',
                'Live ANPR demo on your camera feed',
                'Migration help from existing tools',
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-2.5 text-sm text-slate-700"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#14D4C6] mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#14D4C6]/10 rounded-full blur-3xl pointer-events-none" />
              {submitted ? (
                <div
                  className="text-center py-12"
                  data-testid="lead-gen-success"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h4
                    className="text-xl font-semibold text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Request received
                  </h4>
                  <p className="text-sm text-slate-600">
                    We&apos;ll reach out to <strong>{form.email}</strong> within
                    one business day.
                  </p>
                  {geo.lat != null && geo.lon != null && (
                    <div
                      className="mt-6 max-w-md mx-auto rounded-lg overflow-hidden border border-slate-200"
                      data-testid="lead-success-map"
                    >
                      <iframe
                        title="Confirmed location"
                        className="w-full h-44 block"
                        loading="lazy"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon - 0.01},${geo.lat - 0.008},${geo.lon + 0.01},${geo.lat + 0.008}&layer=mapnik&marker=${geo.lat},${geo.lon}`}
                      />
                      <div className="px-3 py-2 bg-white border-t border-slate-200 flex items-start gap-1.5 text-left">
                        <MapPin className="w-3.5 h-3.5 text-[#14D4C6] mt-0.5 shrink-0" />
                        <p
                          className="text-xs text-slate-600 leading-snug truncate"
                          title={geo.label}
                        >
                          We&apos;ll come prepared for{' '}
                          <strong className="text-slate-900">
                            {form.society_name}
                          </strong>{' '}
                          near {geo.label?.split(',').slice(0, 2).join(',')}.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="lead-gen-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label="Your name"
                      placeholder="Jane Sharma"
                      value={form.name}
                      onChange={set('name')}
                      testid="lead-input-name"
                      required
                    />
                    <Field
                      label="Society name"
                      placeholder="Green Valley Villas"
                      value={form.society_name}
                      onChange={set('society_name')}
                      testid="lead-input-society"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label="Email"
                      type="email"
                      placeholder="you@society.com"
                      value={form.email}
                      onChange={set('email')}
                      testid="lead-input-email"
                      required
                    />
                    <Field
                      label="Phone (optional)"
                      placeholder="+91 98XXX XXXXX"
                      value={form.phone}
                      onChange={set('phone')}
                      testid="lead-input-phone"
                    />
                  </div>

                  {/* Address with live map preview */}
                  <div data-testid="lead-address-block">
                    <label
                      className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      <MapPin className="w-3 h-3" />
                      Society address or pincode (optional)
                      {geocoding && (
                        <Loader2
                          className="w-3 h-3 animate-spin text-slate-400"
                          data-testid="geocode-loading"
                        />
                      )}
                    </label>
                    <input
                      value={form.address}
                      onChange={set('address')}
                      placeholder="e.g., 560066 or Marathahalli, Bengaluru"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14D4C6]/40 focus:border-[#14D4C6] transition-colors"
                      style={{ fontFamily: 'Work Sans, sans-serif' }}
                      data-testid="lead-input-address"
                    />
                    {geo.lat != null && geo.lon != null && (
                      <div
                        className="mt-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 relative"
                        data-testid="lead-map-preview"
                      >
                        <iframe
                          title="Society location preview"
                          className="w-full h-44 sm:h-52 block"
                          loading="lazy"
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon - 0.01},${geo.lat - 0.008},${geo.lon + 0.01},${geo.lat + 0.008}&layer=mapnik&marker=${geo.lat},${geo.lon}`}
                          data-testid="lead-map-iframe"
                        />
                        <div className="px-3 py-2 bg-white border-t border-slate-200 flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#14D4C6] mt-0.5 shrink-0" />
                          <p
                            className="text-xs text-slate-600 leading-snug truncate"
                            title={geo.label}
                            data-testid="lead-map-label"
                          >
                            {geo.label}
                          </p>
                        </div>
                      </div>
                    )}
                    {form.address.trim().length >= 4 &&
                      !geocoding &&
                      geo.lat == null && (
                        <p
                          className="mt-2 text-xs text-amber-600"
                          data-testid="lead-map-not-found"
                        >
                          Couldn&apos;t pin that on the map — no worries, we&apos;ll
                          confirm with you on the call.
                        </p>
                      )}
                  </div>
                  <div>
                    <label
                      className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      What are you hoping to solve? (optional)
                    </label>
                    <textarea
                      value={form.message}
                      onChange={set('message')}
                      rows={4}
                      placeholder="e.g., We want to replace our paper visitor register and add ANPR..."
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14D4C6]/40 focus:border-[#14D4C6] transition-colors"
                      style={{ fontFamily: 'Work Sans, sans-serif' }}
                      data-testid="lead-input-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-semibold px-7 py-3.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="lead-submit-btn"
                  >
                    {submitting ? 'Sending…' : 'Request a demo'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, required, ...rest }) => (
  <div>
    <label
      className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {label}
      {required && <span className="text-rose-500 ml-1">*</span>}
    </label>
    <input
      {...rest}
      data-testid={testid}
      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14D4C6]/40 focus:border-[#14D4C6] transition-colors"
      style={{ fontFamily: 'Work Sans, sans-serif' }}
    />
  </div>
);
